import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { dataService } from '@/services/data-service';
import type { SystemOptions } from '@/types/financials';

interface SystemOptionsContextType {
  options: SystemOptions | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const SystemOptionsContext = createContext<SystemOptionsContextType | undefined>(undefined);

export function SystemOptionsProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<SystemOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dataService.getSystemOptions();
      setOptions(data);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to fetch system options:', err);
      // Set default options on error so app doesn't break
      setOptions({
        configType: 'STANDARD',
        isMultiEntity: false,
        autoNumbering: {
          enabled: false,
          numberOfChars: 10,
          currentNumber: 1,
        },
        validation: {
          noNegativeBoughtValue: false,
        },
        defaults: {
          baseValueOn: 'remaining',
          servicePeriod: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, []);

  return (
    <SystemOptionsContext.Provider value={{ options, loading, error, refetch: fetchOptions }}>
      {children}
    </SystemOptionsContext.Provider>
  );
}

export function useSystemOptions() {
  const context = useContext(SystemOptionsContext);
  if (context === undefined) {
    throw new Error('useSystemOptions must be used within SystemOptionsProvider');
  }
  return context;
}
